import { useState } from "react";
import {
  LifeBuoy, Mail, Phone, BookOpen, ChevronDown,
  Settings as SettingsIcon, Users, Banknote, FileText,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import RequestCallDialog, { SUPPORT_EMAIL } from "@/components/RequestCallDialog";

const guideSections = [
  {
    icon: FileText,
    title: "Foglio cassa e chiusura giornaliera",
    body: "Ogni giorno i collaboratori inseriscono gli incassi nel proprio foglio cassa. Alla chiusura (manuale o automatica all'orario impostato) viene generato un riepilogo PDF + Excel inviato via email. Da Impostazioni puoi configurare gli orari di chiusura per ogni giorno della settimana e l'email di destinazione.",
  },
  {
    icon: Banknote,
    title: "Versamenti in banca",
    body: "I collaboratori versano gli incassi liquidi (contanti, assegni). Dalle Impostazioni puoi scegliere la modalità: 'Per polizze' (con spunte sulle singole polizze) oppure 'Importo libero' (solo importo totale). Ogni versamento può richiedere convalida dell'agente o essere auto-convalidato (es. su conto separato del collaboratore o piano Solo).",
  },
  {
    icon: Users,
    title: "Collaboratori e conto separato",
    body: "Da 'Utenze' crei i collaboratori. Per ogni collaboratore puoi attivare 'Conto separato': in questa modalità gli incassi liquidi vanno sul C/C personale del collaboratore e poi sono rimborsati all'agenzia con una distinta. I metodi 'diretti' (es. bonifico) vanno direttamente all'agenzia senza passare dal C/C personale.",
  },
  {
    icon: SettingsIcon,
    title: "Impostazioni email e PDF",
    body: "Da Impostazioni puoi scegliere se usare l'SMTP della piattaforma (consigliato) o il tuo. Puoi configurare l'orientamento del PDF (verticale/orizzontale), abilitare il foglio cassa POS separato in chiusura, e impostare l'invio automatico di una copia al collaboratore.",
  },
  {
    icon: BookOpen,
    title: "Flusso di cassa e plurimandatari",
    body: "Il 'Flusso di cassa' calcola automaticamente saldo iniziale, incassi liquidi, uscite e versamenti per agente e collaboratori. La gestione 'Plurimandatari' (piani Pro e Unlimited) ti permette di tracciare più compagnie sullo stesso foglio cassa con riepiloghi separati o cumulativi.",
  },
];

export default function Aiuto() {
  const { user } = useAuth();
  const [callOpen, setCallOpen] = useState(false);
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  const defaults = {
    nome: `${user?.nome || ""} ${user?.cognome || ""}`.trim(),
    email: user?.email || "",
    agenzia: user?.agency?.nome_agenzia || "",
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="aiuto-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <LifeBuoy className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Aiuto e assistenza</h1>
          <p className="text-sm text-slate-600 mt-1">
            Tutto quello che ti serve per iniziare e usare al meglio Incassi Cloud.
          </p>
        </div>
      </header>

      {/* Contatti diretti */}
      <Card className="p-6 border-slate-200" data-testid="aiuto-contatti">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">Contattaci</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            data-testid="aiuto-email-link"
          >
            <Mail className="w-5 h-5 text-blue-700 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email assistenza</div>
              <div className="font-mono-num text-slate-900 font-medium mt-0.5">{SUPPORT_EMAIL}</div>
              <div className="text-xs text-slate-500 mt-1">Risposta in 24h lavorative</div>
            </div>
          </a>
          <button
            type="button"
            onClick={() => setCallOpen(true)}
            className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
            data-testid="aiuto-richiedi-call"
          >
            <Phone className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Prenota una call</div>
              <div className="font-medium text-slate-900 mt-0.5">Richiedi una call gratuita</div>
              <div className="text-xs text-slate-500 mt-1">Configurazione assistita, dubbi, domande</div>
            </div>
          </button>
        </div>
      </Card>

      {/* Guida rapida */}
      <Card className="p-6 border-slate-200" data-testid="aiuto-guida">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-1">Guida rapida</h2>
        <p className="text-sm text-slate-500 mb-4">
          Una panoramica delle funzionalità principali. Clicca per espandere ogni sezione.
        </p>
        <div className="space-y-2">
          {guideSections.map((g) => (
            <div key={g.title} className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(g.title)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
                data-testid={`guida-toggle-${g.title.split(" ")[0].toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <g.icon className="w-5 h-5 text-slate-700" />
                  <span className="font-medium text-slate-900">{g.title}</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-slate-500 transition-transform ${open[g.title] ? "rotate-180" : ""}`}
                />
              </button>
              {open[g.title] && (
                <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{g.body}</div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-600">Hai ancora dubbi?</span>
          <Button size="sm" variant="outline" onClick={() => setCallOpen(true)} data-testid="aiuto-cta-call">
            <Phone className="w-4 h-4 mr-2" /> Richiedi una call
          </Button>
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            <Button size="sm" variant="outline" data-testid="aiuto-cta-email">
              <Mail className="w-4 h-4 mr-2" /> Scrivici a {SUPPORT_EMAIL}
            </Button>
          </a>
        </div>
      </Card>

      <RequestCallDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        source="aiuto"
        defaults={defaults}
      />
    </div>
  );
}
